/**
 * NOS-90: Gap inserter between sections. "+ Section" button on hover, opens catalog.
 * All values from docs/BUILDER_DESIGN_SPEC.md Section 6.
 */
import { useState, useMemo, useEffect } from "react"
import { SectionCatalog } from "./SectionCatalog"
import type { PageSection } from "@/templates/types"

/** Section types with dark backgrounds (per spec Section 5) */
const DARK_SECTION_TYPES = new Set(["hero", "donation_cta", "footer"])

export interface SectionGapInserterProps {
  afterIndex: number
  sections: PageSection[]
  primaryColor: string
  onAddSection: (sectionType: string, afterIndex: number) => Promise<string | null>
  onSelectSection: (sectionId: string) => void
  onCatalogOpen?: () => void
  catalogShouldClose?: boolean
}

export function SectionGapInserter({
  afterIndex,
  sections,
  primaryColor,
  onAddSection,
  onSelectSection,
  onCatalogOpen,
  catalogShouldClose,
}: SectionGapInserterProps) {
  const [catalogOpen, setCatalogOpen] = useState(false)

  useEffect(() => {
    if (catalogShouldClose) setCatalogOpen(false)
  }, [catalogShouldClose])

  const isOverDarkBackground = useMemo(() => {
    const above = sections[afterIndex]
    const below = sections[afterIndex + 1]
    return (
      DARK_SECTION_TYPES.has(above?.section_type ?? "") ||
      DARK_SECTION_TYPES.has(below?.section_type ?? "")
    )
  }, [sections, afterIndex])

  const handleSelect = async (sectionType: string, idx: number) => {
    const newId = await onAddSection(sectionType, idx)
    if (newId) {
      onSelectSection(newId)
    }
  }

  return (
    <div
      className="section-gap"
      style={{
        height: 0,
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        className="gap-btn"
        data-catalog-open={catalogOpen ? "true" : undefined}
        data-dark-bg={isOverDarkBackground ? "true" : undefined}
        onClick={(e) => {
          e.stopPropagation()
          setCatalogOpen((v) => {
            const next = !v
            if (next) onCatalogOpen?.()
            return next
          })
        }}
        aria-label="Add section"
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: catalogOpen ? 1 : 0,
          padding: "4px 12px",
          borderRadius: 20,
          fontSize: 10,
          fontWeight: 600,
          zIndex: 20,
          cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: catalogOpen
            ? "0 6px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08)"
            : undefined,
          ...(catalogOpen
            ? isOverDarkBackground
              ? {
                  background: primaryColor,
                  border: `1px solid ${primaryColor}`,
                  color: "#fff",
                }
              : {
                  background: "#0f0f14",
                  border: "1px solid #0f0f14",
                  color: "#fff",
                }
            : {
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.4)",
              }),
        }}
      >
        + Section
      </button>
      {catalogOpen && (
        <>
          {/* Backdrop — catches clicks outside catalog (Bug 5) */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 19 }}
            onClick={() => setCatalogOpen(false)}
            aria-hidden="true"
          />
          <SectionCatalog
            sections={sections}
            primaryColor={primaryColor}
            gapAfterIndex={afterIndex}
            onSelect={handleSelect}
            onClose={() => setCatalogOpen(false)}
          />
        </>
      )}
      {/* Hover: reveal button; when catalog closed, contrast-aware (black on light, white on dark) */}
      <style>{`
        .section-gap:hover .gap-btn {
          opacity: 1 !important;
        }
        .section-gap:hover .gap-btn:not([data-catalog-open="true"]) {
          box-shadow: 0 6px 20px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08) !important;
        }
        .section-gap:hover .gap-btn:not([data-catalog-open="true"])[data-dark-bg="true"] {
          background: #fff !important;
          color: #0f0f14 !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        .section-gap:hover .gap-btn:not([data-catalog-open="true"]):not([data-dark-bg="true"]) {
          background: #0f0f14 !important;
          color: #fff !important;
          border-color: rgba(15,15,20,0.8) !important;
        }
      `}</style>
    </div>
  )
}
